export enum PreferenceCategory {
	StrictlyNecessary = 'STRICTLY_NECESSARY',
	Functional = 'FUNCTIONAL',
	Marketing = 'MARKETING',
	Analytics = 'ANALYTICS',
	Unknown = 'UNKNOWN',
}

export enum ConsentPreferenceV2 {
	ConsentDataUnavailable = 'CONSENT_DATA_UNAVAILABLE',
	ConsentedUnderGDPR = 'CONSENTS_SAVED_IN_GDPR_LOCALE',
	ConsentedUnderCPRA = 'CONSENTS_SAVED_IN_CPRA_LOCALE',
	ConsentedUnderLGPD = 'CONSENTS_SAVED_IN_LGPD_LOCALE',
	ConsentedUnderFADP = 'CONSENTS_SAVED_IN_FADP_LOCALE',
}

export enum ContextualConsentProperty {
	ConsentsAreDefault = 'DEFAULT_CONSENTS',
	UserIsAuthenticated = 'USER_AUTHENTICATED',
	ConsentDataUnavailable = 'CONSENT_DATA_UNAVAILABLE',
}

export type CookiePreferences = {
	[key in PreferenceCategory]: boolean;
};

export type AdditionalConsentPreferencesV2 = {
	[key in ConsentPreferenceV2]: boolean;
};

export type ContextualConsentPreferences = {
	[key in ContextualConsentProperty]: boolean;
};

export type ConsentPreferences =
	| CookiePreferences // V1
	| (CookiePreferences & AdditionalConsentPreferencesV2) // V2
	| (CookiePreferences & ContextualConsentPreferences); // V3

type Keys = {
	[name: string]: number;
};

type Pattern = { [name: string]: Pattern | number };

export type FlatPattern = { [prefix: string]: number };

/**
 * The pattern layout of a storage-controls cache payload: `'flat'` for the
 * `categories_v2_<storageType>.json` prefix/suffix maps, `'trie'` for the legacy
 * `categories_<storageType>.json` character tries.
 *
 * TEMPORARY: Will be removed when the related feature flags are cleaned up (`platform_bsc_ssr_flat_pattern_file_format`,
 * `platform_bsc_flat_pattern_file_structure`)
 */
export type StorageControlsPatternFormat = 'flat' | 'trie';

/**
 * A fetched storage-controls cache, without committing to how its patterns are laid out.
 *
 * This is the type to hold, pass around and store: it is what the cache loaders return, what
 * bsc-global-state carries, and what products hand back to `getItemCategoryFromPassedCache`. The
 * per-product `patterns` values are deliberately `unknown` here — a consumer that only couriers the
 * payload should not have to describe a layout it never reads.
 *
 * To read patterns, narrow to {@link TrieCategorizedStorageControlsCache} or
 * {@link FlatCategorizedStorageControlsCache} on `patternFormat`.
 *
 *  TEMPORARY: Will be merged with FlatCategorizedStorageControlsCache when the related feature flags are cleaned up
 * (`platform_bsc_ssr_flat_pattern_file_format`, `platform_bsc_flat_pattern_file_structure`)
 */
export interface CategorizedStorageControlsCache {
	categories: {
		[PreferenceCategory.StrictlyNecessary]: number;
		[PreferenceCategory.Functional]: number;
		[PreferenceCategory.Analytics]: number;
		[PreferenceCategory.Marketing]: number;
		[PreferenceCategory.Unknown]: number;
	};
	keys: {
		[productName: 'all' | string]: Keys;
	};
	thirdPartyKeys: Keys;
	/**
	 * Recorded when this payload is fetched, describing the layout of `patterns` below. Consumers
	 * read this instead of re-evaluating the flat-format rollout gate, which can resolve to a
	 * different value than the one that chose the file.
	 *
	 * Set automatically for payloads fetched by this package. If you fetch the category file
	 * yourself, record it — `getFetchPatternFormat` reports the layout that
	 * `getCategoryCacheUrlPerimeterSSR` points at. Absent means the legacy trie layout.
	 */
	patternFormat?: StorageControlsPatternFormat;
	patterns: {
		startsWith: {
			[productName: 'all' | string]: unknown;
		};
		endsWith: {
			[productName: 'all' | string]: unknown;
		};
		regexp?: {
			[productName: 'all' | string]: { [regexString: string]: number };
		};
	};
	analyticsSamplingRatio?: {
		[name: string]: number;
	};
}

/**
 * The legacy `categories_<storageType>.json` layout: each pattern is a chain of trie nodes, one per
 * character.
 */
export interface TrieCategorizedStorageControlsCache extends CategorizedStorageControlsCache {
	patternFormat: 'trie';
	patterns: {
		startsWith: {
			[productName: 'all' | string]: Pattern;
		};
		endsWith: {
			[productName: 'all' | string]: Pattern;
		};
		regexp?: {
			[productName: 'all' | string]: { [regexString: string]: number };
		};
	};
}

/**
 * The `categories_v2_<storageType>.json` layout: whole prefixes and suffixes mapped straight to a
 * category number.
 */
export interface FlatCategorizedStorageControlsCache extends CategorizedStorageControlsCache {
	patternFormat: 'flat';
	patterns: {
		startsWith: {
			[productName: 'all' | string]: FlatPattern;
		};
		endsWith: {
			[productName: 'all' | string]: FlatPattern;
		};
		regexp?: {
			[productName: 'all' | string]: { [regexString: string]: number };
		};
	};
}

export interface CategorizedStorageControlsCacheLegacy {
	categories: {
		[PreferenceCategory.StrictlyNecessary]: number;
		[PreferenceCategory.Functional]: number;
		[PreferenceCategory.Analytics]: number;
		[PreferenceCategory.Marketing]: number;
		[PreferenceCategory.Unknown]: number;
	};
	[CachedKeysDataPropertyLegacy.Cookies]: Keys;
	[CachedKeysDataPropertyLegacy.ThirdParty]: Keys;
	[CachedKeysDataPropertyLegacy.LocalStorage]: Keys;
	[CachedKeysDataPropertyLegacy.SessionStorage]: Keys;
	patterns: {
		startsWith: Pattern;
	};
	analyticsSamplingRatio?: {
		[name: string]: number;
	};
}

export enum CachedKeysDataPropertyLegacy {
	LocalStorage = 'localStorageKeys',
	SessionStorage = 'sessionStorageKeys',
	Cookies = 'keys',
	ThirdParty = 'thirdPartyKeys',
}

export interface ConsentPreferenceV001 {
	[PreferenceCategory.StrictlyNecessary]: boolean;
	[PreferenceCategory.Functional]: boolean;
	[PreferenceCategory.Analytics]: boolean;
	[PreferenceCategory.Marketing]: boolean;
	[PreferenceCategory.Unknown]: boolean;
}

export interface ConsentPreferenceV002 extends ConsentPreferenceV001 {
	[ContextualConsentProperty.ConsentDataUnavailable]: boolean;
	[ConsentPreferenceV2.ConsentedUnderGDPR]: boolean;
	[ConsentPreferenceV2.ConsentedUnderCPRA]: boolean;
	[ConsentPreferenceV2.ConsentedUnderLGPD]: boolean;
	[ConsentPreferenceV2.ConsentedUnderFADP]: boolean;
}
export interface ConsentPreferenceV003 extends ConsentPreferenceV001 {
	[ContextualConsentProperty.ConsentsAreDefault]: boolean;
	[ContextualConsentProperty.UserIsAuthenticated]: boolean;
	[ContextualConsentProperty.ConsentDataUnavailable]: boolean;
}

export type ConsentPreference =
	| ConsentPreferenceV001
	| ConsentPreferenceV002
	| ConsentPreferenceV003;

// TODO: Can we remove this, or does that break backwards compatibility?
export type CookiePreference = ConsentPreference;

export enum PrefetchedPreferenceState {
	INTERNAL,
}

export interface InitializedPrefsData {
	prefs: ConsentPreference | undefined;
	/**
	 * If true, we need to refetch the preferences as the initial prefetched state was internal
	 * or the initial preferences were missing
	 */
	isPrefetchedPrefsInternalState: boolean;
}

export enum CategoryStorageType {
	Cookies = 'Cookies',
	LocalStorage = 'LocalStorage',
	SessionStorage = 'SessionStorage',
	IndexedDB = 'IndexedDB',
}

export enum Status {
	SUCCESS = 'SUCCESS',
	BLOCKED = 'BLOCKED',
	FAILED = 'FAILED',
	ERROR = 'ERROR',
}

export type StatusValue = `${Status}`;

export type SetItemResult =
	| {
			status: Exclude<StatusValue, 'ERROR' | 'BLOCKED'>;
	  }
	| {
			status: 'BLOCKED';
			/**
			 * The category the key is registered under, when the block reason could be determined.
			 * `undefined` = synchronous (MaybeSync) path; block reason unavailable (no
			 * `blockedCallback` supported). `null` = key not registered on the allow-list.
			 * `PreferenceCategory` = registered but blocked by user consent preferences.
			 */
			blockedItemCategory?: PreferenceCategory | null;
	  }
	| {
			status: 'ERROR';
			error: Error;
	  };

export type StorageCleanupStorageType = 'localStorage' | 'sessionStorage' | 'cookie' | 'indexedDB';

export type StorageCleanupDeletionMode = 'live' | 'dry-run';

export type StorageCleanupItem = {
	key: string;
	storageType: StorageCleanupStorageType;
	category: PreferenceCategory;
	/**
	 * `undefined` in dry-run, where nothing is attempted.
	 * `false` if an attempted delete failed.
	 */
	deleted?: boolean;
};

/** Why a whole storage type was not enumerated during a cleanup run. */
export type StorageCleanupSkipReason =
	/** The category cache for this storage type failed to load, so nothing could be classified. */
	| 'cacheUnavailable'
	/** The per-run enumeration cap was already exhausted before this storage type was reached. */
	| 'enumerationLimit'
	/** The browser does not expose the API needed to enumerate it (e.g. `indexedDB.databases`). */
	| 'unsupported'
	/** Enumeration threw. */
	| 'error';

export type StorageCleanupSkippedType = {
	storageType: StorageCleanupStorageType;
	reason: StorageCleanupSkipReason;
};

export type UncategorizedStorageItem = {
	key: string;
	storageType: StorageCleanupStorageType;
};

export type StorageCleanupResult = {
	deletionMode: StorageCleanupDeletionMode;
	/** Keys whose category was revoked. Deleted in live mode; reported only in dry-run. */
	revokedItems: StorageCleanupItem[];
	/** Keys present in storage but not registered in any category cache. Never deleted. */
	uncategorizedItems: UncategorizedStorageItem[];
	/**
	 * True when the total number of enumerated keys across all storage types reached the
	 * per-run cap (ENUMERATION_LIMIT). Items beyond the cap are silently skipped.
	 */
	hitEnumerationLimit: boolean;
	/**
	 * Storage types not enumerated at all. A type that was entered and then truncated by the cap is
	 * not listed — `hitEnumerationLimit` covers that.
	 *
	 * A non-empty value means the other fields are partial: `revokedItems` cannot be read as a
	 * complete picture of what is present. `undefined` when the run failed before enumeration
	 * finished.
	 */
	skippedTypes?: StorageCleanupSkippedType[];
	/**
	 * Cleanup assumes a top-level context: consent is collected per top-level site, and a framed
	 * context can have a different cookie jar and storage partition. Results from a framed run are
	 * therefore not comparable. `undefined` when the run failed before enumeration finished.
	 */
	inFrame?: boolean;
};

export type BscInitializationResult = {
	/**
	 * A Promise that resolves with the full cleanup result once the
	 * enumerate→classify→(delete) loop completes. Boxed in an object so the
	 * caller can chain `.then()` without JS auto-flattening the Promise.
	 */
	storageCleanupPromise: Promise<StorageCleanupResult>;
};

// List of BSC package killsitches
export enum BSC_PACKAGE_KILLSWITCHES {
	MASTER_KILLSWITCH = 'platform_moonjelly_bsc_master_killswitch',
	UI_KILLSWITCH = 'platform_moonjelly_bsc_ui_killswitch',
	SSR_KILLSWITCH = 'platform_moonjelly_bsc_ssr_killswitch',
	WEB_STORAGE_KILLSWITCH = 'platform_moonjelly_bsc_web_storage_killswitch',
}

/**
 * Common fields that are shared between the ConsentBanner and initializeControls
 */
export type CommonConsentBannerProps = {
	/**
	 * When `analyticsEnabled` is set to true, analytics events will be sent to gas services.
	 * When it is disabled, no analytics events will be sent. Disable for proudcts which are not integrated
	 * with GAS.
	 *
	 * @default true
	 */
	analyticsEnabled?: boolean;

	/**
	 * When 'localConsentMode' is turned on, the banner only saves and reads cookie preferences from the cookie in the browser.
	 * No calls will be made to ConsentHub for looking up preferences, geo-IP, or saving preferences. All users, regardless of
	 * geo, will receive the banner.
	 *
	 * Note: This field will be ignored and forced to `true` in the FedRAMP Moderate perimeter, as ConsentHub does not currently
	 * exist in FedRAMP Moderate perimeter.
	 *
	 * Defaults to false, so that only consumers who are unable to integrate with ConsentHub need to set this mode.
	 * @default false
	 */
	localConsentMode?: boolean;

	/** Name of the product that is rendering the component/initializing controls */
	product?: string;
};

/**
 * shouldRenderBannerPrefetched uses several browser APIs that impact first render.
 * This is a losing battle for hydration and VC90 mutations. So instead of try to match up
 * the "prefetchedPreferencesData" with what we expect the browser APIs to give us, instead
 * we can use the SSRFriendlyBannerOptions to give compatible APIs for the browser in SSR.
 *
 * Since isSSR and getCookie are the only two APIs used for first paint, these are the only ones passed through.
 */
export interface SSRFriendlyBannerOptions {
	isSSR?: boolean;
	getCookie?: (name: string) => string | undefined;
}
